import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
// Đảm bảo bạn import đúng hàm lấy danh sách đơn hàng đã được điều chỉnh
// và các hàm khác nếu có API backend tương ứng (ví dụ: API hủy đơn)
import { fetchOrdersByUserIdFromBackend /*, cancelOrderOnBackend */ } from '../services/orderService'; // Đổi tên service và hàm cho phù hợp
import { formatCurrency } from '../utils/formatters'; // Giữ lại hàm format tiền tệ

// CSS (có thể đặt trong file CSS riêng hoặc <style> tag trong component)
const styles = `
    .order-item-header {
        cursor: pointer;
        padding: 1rem 1.25rem;
        background-color: #f8f9fa;
        border-bottom: 1px solid rgba(0,0,0,.125);
    }
    .order-item-header:hover {
        background-color: #e9ecef;
    }
    .order-item-body {
        padding: 1rem 1.25rem;
    }
    .badge-status {
        font-size: 0.9em;
    }
    .product-item-details img {
        max-width: 60px;
        margin-right: 10px;
    }
`;

function OrdersPage() {
    const { user, token } = useAuth(); // Lấy cả token để đảm bảo có thể gọi API
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [cancelLoading, setCancelLoading] = useState({});
    const [expandedOrderId, setExpandedOrderId] = useState(null); // ID của đơn hàng đang được mở rộng

    useEffect(() => {
        const fetchOrders = async () => {
            if (!user?.id || !token) {
                setError('Vui lòng đăng nhập để xem đơn hàng.');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // Gọi hàm đã được điều chỉnh để lấy danh sách đơn hàng từ backend
                const rawOrders = await fetchOrdersByUserIdFromBackend(user.id);

                // Parse trường 'data' của mỗi đơn hàng
                const processedOrders = rawOrders.map(order => {
                    let parsedData = {};
                    try {
                        if (order.data) {
                            parsedData = JSON.parse(order.data);
                        }
                    } catch (e) {
                        console.error(`Lỗi parse JSON cho đơn hàng ID ${order.id}:`, e);
                        parsedData = { error_parsing: "Dữ liệu đơn hàng không hợp lệ" };
                    }
                    return {
                        ...order, // Giữ lại các trường gốc như id, user
                        parsedData: parsedData // Thêm dữ liệu đã parse
                    };
                });

                setOrders(processedOrders);
                setError('');
            } catch (err) {
                setError(err.message || 'Không thể tải danh sách đơn hàng.');
                console.error("Lỗi khi tải đơn hàng:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchOrders();
    }, [user, token]); // Thêm token vào dependencies

    const toggleExpandOrder = (orderId) => {
        setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
    };

    // Hàm hủy đơn hàng (CẦN API BACKEND TƯƠNG ỨNG)
    const handleCancelOrder = async (orderId, e) => {
        e.stopPropagation(); // Ngăn sự kiện click lan ra việc mở/đóng accordion
        if (!window.confirm("Bạn có chắc chắn muốn hủy đơn hàng này?")) {
            return;
        }

        // Ví dụ: Nếu bạn có API /data/order/{orderId}/cancel (METHOD: POST hoặc PUT)
        // setCancelLoading(prev => ({ ...prev, [orderId]: true }));
        // try {
        //     // const response = await apiClient.post(`/data/order/${orderId}/cancel`); //Sử dụng apiClient
        //     // if (response.data.success) { // Hoặc kiểm tra theo response backend
        //     //     // Cập nhật trạng thái đơn hàng trong list orders
        //     //     setOrders(prevOrders => prevOrders.map(o =>
        //     //         o.id === orderId ? { ...o, parsedData: { ...o.parsedData, status: 'cancelled' } } : o
        //     //     ));
        //     //     alert("Đơn hàng đã được hủy thành công.");
        //     // } else {
        //     //     alert(response.data.message || "Không thể hủy đơn hàng.");
        //     // }
        //     alert(`Chức năng hủy đơn hàng ID ${orderId} chưa được triển khai với API backend.`);
        // } catch (err) {
        //     alert(err.message || 'Lỗi khi hủy đơn hàng.');
        //     console.error("Lỗi hủy đơn hàng:", err);
        // } finally {
        //     setCancelLoading(prev => ({ ...prev, [orderId]: false }));
        // }
        alert(`Chức năng hủy đơn hàng (ID: ${orderId}) cần API backend hỗ trợ.`);
        // Tạm thời chỉ log ra console
        console.log(`Yêu cầu hủy đơn hàng ID: ${orderId}`);
    };

    const getStatusTextAndClass = (status) => {
        status = status ? status.toLowerCase() : 'pending'; // Mặc định là pending nếu status null/undefined
        switch (status) {
            case 'processing':
                return { text: 'Đang xử lý', className: 'badge bg-warning text-dark' };
            case 'shipped':
                return { text: 'Đang giao', className: 'badge bg-info text-dark' };
            case 'delivered':
                return { text: 'Đã giao', className: 'badge bg-success' };
            case 'cancelled':
            case 'canceled': // Chấp nhận cả 2 spelling
                return { text: 'Đã hủy', className: 'badge bg-danger' };
            case 'pending':
                return { text: 'Chờ xác nhận', className: 'badge bg-secondary'};
            default:
                return { text: status, className: 'badge bg-light text-dark' };
        }
    };

    if (!user) {
        return <div className="container my-5"><div className="alert alert-warning">Vui lòng đăng nhập để xem đơn hàng.</div></div>;
    }

    return (
        <>
            <style>{styles}</style> {/* Add styles */}
            <div className="container my-5">
                <h2 className="mb-4">Lịch sử đơn hàng của bạn</h2>

                {loading && (
                    <div className="d-flex justify-content-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Đang tải...</span>
                        </div>
                    </div>
                )}
                {error && <div className="alert alert-danger">{error}</div>}

                {!loading && orders.length === 0 && !error && (
                    <div className="alert alert-info">Bạn chưa có đơn hàng nào.</div>
                )}

                {!loading && orders.length > 0 && (
                    <div className="accordion" id="ordersAccordion">
                        {orders.map((order) => {
                            const { text: statusText, className: statusClass } = getStatusTextAndClass(order.parsedData.status);
                            const orderDate = order.parsedData.orderDate || order.createdAt || order.updatedAt; // Tìm một trường ngày hợp lệ
                            
                            return (
                                <div className="accordion-item" key={order.id}>
                                    <h2 className="accordion-header" id={`heading-${order.id}`}>
                                        <button
                                            className={`accordion-button ${expandedOrderId === order.id ? '' : 'collapsed'}`}
                                            type="button"
                                            onClick={() => toggleExpandOrder(order.id)}
                                            aria-expanded={expandedOrderId === order.id}
                                            aria-controls={`collapse-${order.id}`}
                                        >
                                            <div className="d-flex w-100 justify-content-between align-items-center">
                                                <div>
                                                    <span className="fw-bold">Mã đơn: #{order.id}</span>
                                                    <small className="ms-2 text-muted">
                                                        {orderDate ? new Date(orderDate).toLocaleDateString('vi-VN') : 'N/A'}
                                                    </small>
                                                </div>
                                                <span className={`badge ${statusClass}`}>{statusText}</span>
                                            </div>
                                        </button>
                                    </h2>
                                    <div
                                        id={`collapse-${order.id}`}
                                        className={`accordion-collapse collapse ${expandedOrderId === order.id ? 'show' : ''}`}
                                        aria-labelledby={`heading-${order.id}`}
                                        data-bs-parent="#ordersAccordion"
                                    >
                                        <div className="accordion-body">
                                            <div className="row">
                                                <div className="col-md-8">
                                                    <h5>Chi tiết sản phẩm:</h5>
                                                    {order.parsedData.items && order.parsedData.items.length > 0 ? (
                                                        <ul className="list-group list-group-flush mb-3">
                                                            {order.parsedData.items.map((item, index) => (
                                                                <li key={item.productId || index} className="list-group-item d-flex justify-content-between align-items-center product-item-details">
                                                                    <div>
                                                                        {/* Bạn có thể thêm ảnh nếu có item.imageUrl */}
                                                                        {/* <img src={item.imageUrl || 'https://via.placeholder.com/50'} alt={item.name} className="img-thumbnail" /> */}
                                                                        <span className="fw-bold">{item.name}</span>
                                                                        <small className="d-block text-muted">SL: {item.quantity} x {formatCurrency(item.price)}</small>
                                                                    </div>
                                                                    <span>{formatCurrency(item.quantity * item.price)}</span>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p>Không có thông tin sản phẩm.</p>
                                                    )}

                                                    {/* Thông tin giao hàng, thanh toán, v.v. có thể thêm ở đây */}
                                                    {order.parsedData.shippingInfo && (
                                                        <>
                                                        <h5>Thông tin giao hàng:</h5>
                                                        <p className="mb-1"><strong>Tên người nhận:</strong> {order.parsedData.shippingInfo.name || user.fullName}</p>
                                                        <p className="mb-1"><strong>Địa chỉ:</strong> {order.parsedData.shippingInfo.address || user.address}</p>
                                                        <p className="mb-1"><strong>Điện thoại:</strong> {order.parsedData.shippingInfo.phone || user.phone}</p>
                                                        {order.parsedData.shippingInfo.notes && <p className="mb-1"><strong>Ghi chú:</strong> {order.parsedData.shippingInfo.notes}</p>}
                                                        </>
                                                    )}
                                                </div>
                                                <div className="col-md-4">
                                                    <h5>Tổng kết đơn hàng:</h5>
                                                    <p className="mb-1"><strong>Tạm tính:</strong> {formatCurrency(order.parsedData.subtotal || 0)}</p>
                                                    <p className="mb-1"><strong>Phí vận chuyển:</strong> {formatCurrency(order.parsedData.shippingCost || 0)}</p>
                                                    {order.parsedData.discountAmount > 0 && (
                                                        <p className="mb-1"><strong>Giảm giá:</strong> -{formatCurrency(order.parsedData.discountAmount)}</p>
                                                    )}
                                                    <hr/>
                                                    <p className="mb-1 fw-bold fs-5"><strong>Tổng cộng:</strong> {formatCurrency(order.parsedData.totalAmount)}</p>
                                                    <p className="mb-1"><strong>Thanh toán:</strong> {order.parsedData.paymentMethod === 'cod' ? 'Khi nhận hàng (COD)' : (order.parsedData.paymentMethod || 'N/A')}</p>
                                                    
                                                    {/* Nút hủy đơn */}
                                                    {(order.parsedData.status === 'processing' || order.parsedData.status === 'pending') && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger mt-3"
                                                            onClick={(e) => handleCancelOrder(order.id, e)}
                                                            disabled={cancelLoading[order.id]}
                                                        >
                                                            {cancelLoading[order.id] ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    Đang hủy...
                                                                </>
                                                            ) : 'Hủy đơn hàng'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </>
    );
}

export default OrdersPage;